import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ActiveDirectoryEmailSearch,
  UserEmailSearch,
  UserItemApprove,
  UserItemCreate,
  UserItemDelete,
  UserItemInitialState,
  UserItemLoad,
  UserItemResetPassword,
  UserItemRolesLoad,
  UserItemUpdate,
} from './user-item.actions';
import { UserEmailSearchModel, UserModel } from '../models/user.model';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { UserItemService } from './user-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { UserRolesModel } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface UserItemStateModel {
  user: UserModel;
  roleList: UserRolesModel;
  emailSearchResult: UserEmailSearchModel;
  emailSearchResultInActiveDirectory: UserEmailSearchModel;
  loaded: boolean;
}

const initialState: UserItemStateModel = {
  user: new UserModel(),
  emailSearchResult: null,
  emailSearchResultInActiveDirectory: null,
  roleList: null,
  loaded: false,
};

@State<UserItemStateModel>({
  name: 'userItem',
  defaults: initialState,
})
@Injectable()
export class UserItemState {
  constructor(
    private userItemService: UserItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: UserItemStateModel): UserItemStateModel {
    return state;
  }

  @Selector()
  static getUserData(state: UserItemStateModel): UserModel {
    return state.user;
  }

  @Selector()
  static getUserId(state: UserItemStateModel): number {
    return state.user && state.user.id;
  }

  @Selector()
  static getEmailSearchResult(state: UserItemStateModel): UserEmailSearchModel {
    return state.emailSearchResult;
  }

  @Selector()
  static getEmailSearchInActiveDirectoryResult(state: UserItemStateModel): UserEmailSearchModel {
    return state.emailSearchResultInActiveDirectory;
  }

  @Selector()
  static getUserName(state: UserItemStateModel): string | null {
    return state.user && state.user.firstName && state.user.lastName
      ? `${state.user.firstName} ${state.user.lastName}`
      : null;
  }

  @Selector()
  static getLoaded(state: UserItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getRoleList(state: UserItemStateModel): UserRolesModel {
    return state.roleList;
  }

  @Action(UserItemLoad)
  getUserDetails(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userItemService.getUserDetails(payload).pipe(
      take(1),
      map((userDetails: UserModel) => {
        ctx.patchState({ user: userDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserItemRolesLoad)
  getUserRoleList(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemRolesLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userItemService.getUserRoleList(payload).pipe(
      take(1),
      map((roleList: UserRolesModel) => ctx.patchState({ roleList })),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserItemApprove)
  approveUser(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemApprove,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userItemService.approveUser(payload.userId, payload.role).pipe(
      take(1),
      map((userDetails: UserModel) => {
        this.notificationService.success('api.user.success.approve');
        ctx.patchState({ user: userDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserItemCreate)
  createUser(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userItemService.createUser(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.user.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/user-management/users');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserItemUpdate)
  updateUser(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userItemService.updateUser(payload).pipe(
      take(1),
      map((userDetails: UserModel) => {
        this.notificationService.success('api.user.success.update');
        ctx.patchState({ user: userDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserEmailSearch)
  searchEmail(ctx: StateContext<UserItemStateModel>, { payload }: UserEmailSearch): void {
    this.userItemService.isEmailExist(payload).subscribe((exist: boolean) => {
      ctx.patchState({ emailSearchResult: new UserEmailSearchModel({ email: payload, exist }) });
    });
  }

  @Action(ActiveDirectoryEmailSearch)
  searchEmailInActiveDirectory(ctx: StateContext<UserItemStateModel>, { payload }: ActiveDirectoryEmailSearch): void {
    this.userItemService.isEmailExistInActiveDirectory(payload).subscribe((exist: boolean) => {
      ctx.patchState({
        emailSearchResultInActiveDirectory: new UserEmailSearchModel({ email: payload, exist }),
      });
    });
  }

  @Action(UserItemResetPassword)
  resetUserPassword(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemResetPassword,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.userItemService.resetUserPassword(payload.userId).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.user.success.resetPassword');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserItemDelete)
  deleteUser(
    ctx: StateContext<UserItemStateModel>,
    { payload }: UserItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.userItemService.deleteUsers(payload.userIds).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.user.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/user-management/users');
        return ctx.dispatch(new UserItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UserItemInitialState)
  setInitialState(ctx: StateContext<UserItemStateModel>): void {
    ctx.setState(initialState);
  }
}
